import { useState, useEffect, useRef, useCallback } from 'react';
import { Lead, LeadEvent } from '../types';

/**
 * REALTIME WEBSOCKET SYNC EXPLANATION:
 * ===================================
 * 
 * ARCHITECTURE:
 * 1. React app connects to WebSocket endpoint (ws://localhost:8000/ws/leads)
 * 2. Backend broadcasts events when any user creates/updates leads via API
 * 3. All connected clients receive the same events simultaneously
 * 4. React updates local state immediately without refetching from API
 * 
 * FLOW:
 * User A edits lead → API saves to DB → Redis event published → 
 * WebSocket broadcasts to all clients → User B/C/D see instant update
 * 
 * BENEFITS:
 * - No polling required
 * - Sub-second updates across all browsers
 * - Collaborative editing experience
 * - Minimal network traffic
 */

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

interface UseWebSocketOptions {
  onLeadCreated?: (lead: Lead) => void;
  onLeadUpdated?: (leadId: string, updatedFields: Partial<Lead>) => void;
  onConnectionStatusChange?: (status: ConnectionStatus) => void;
}

/**
 * Custom hook for managing WebSocket connection and real-time lead updates
 */
export const useWebSocket = (options: UseWebSocketOptions = {}) => {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const [lastEvent, setLastEvent] = useState<LeadEvent | null>(null);
  
  // Use refs to avoid stale closure issues with WebSocket callbacks
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 15; // Increased to handle slow backend startup
  
  // Store callbacks in refs to avoid reconnection loops
  const optionsRef = useRef(options);
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const updateConnectionStatus = useCallback((status: ConnectionStatus) => {
    setConnectionStatus(status);
    optionsRef.current.onConnectionStatusChange?.(status);
  }, []);

  /**
   * Handle incoming WebSocket messages
   * REALTIME SYNC: This is where live updates are processed
   */
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      // Skip plain text responses like "pong"
      if (typeof event.data === 'string' && event.data === 'pong') {
        return; // Ignore pong responses
      }

      const data: LeadEvent = JSON.parse(event.data);
      setLastEvent(data);

      // COLLABORATIVE UPDATES: Process different event types
      switch (data.event_type) {
        case 'lead_created':
          // Another user created a lead → add to our table
          if (data.data?.lead) {
            optionsRef.current.onLeadCreated?.(data.data.lead);
          }
          break;

        case 'lead_updated':
          // Another user updated a lead → merge changes into our table
          if (data.lead_id && data.data?.updated_fields) {
            optionsRef.current.onLeadUpdated?.(data.lead_id, data.data.updated_fields);
          }
          break;

        case 'connection_established':
          console.log('WebSocket connection established:', data.message);
          break;

        default:
          console.log('Unknown event type:', data.event_type);
      }

    } catch (error) {
      // Silently ignore parsing errors for non-JSON messages
      if (error instanceof SyntaxError && event.data !== 'pong') {
        console.warn('Received non-JSON WebSocket message:', event.data);
      }
    }
  }, []);

  /**
   * Establish WebSocket connection with automatic reconnection
   */
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    try {
      updateConnectionStatus('connecting');
      
      // WEBSOCKET CONNECTION: Connect to backend real-time endpoint
      // Use environment variable for production WebSocket URL
      const wsUrl = process.env.REACT_APP_WS_URL 
        ? `${process.env.REACT_APP_WS_URL}/ws/leads`
        : 'ws://localhost:8001/ws/leads';
      
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        updateConnectionStatus('connected');
        reconnectAttempts.current = 0; // Reset reconnect counter
      };

      wsRef.current.onmessage = handleMessage;

      wsRef.current.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        wsRef.current = null;
        updateConnectionStatus('disconnected');
        
        // AUTO-RECONNECTION: Attempt to reconnect with exponential backoff
        // Only reconnect if it wasn't a manual disconnect (code 1000)
        if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(Math.pow(2, reconnectAttempts.current) * 1000, 10000); // Max 10s
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1}/${maxReconnectAttempts})`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        } else if (reconnectAttempts.current >= maxReconnectAttempts) {
          console.log('Max reconnection attempts reached');
          updateConnectionStatus('error');
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        updateConnectionStatus('error');
      };

    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      updateConnectionStatus('error');
    }
  }, [updateConnectionStatus, handleMessage]);

  /**
   * Disconnect and cleanup WebSocket
   */
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
      wsRef.current.close(1000, 'Manual disconnect'); // Normal closure
      wsRef.current = null;
    }
    
    reconnectAttempts.current = maxReconnectAttempts; // Prevent reconnection
    updateConnectionStatus('disconnected');
  }, [updateConnectionStatus]);

  /**
   * Send a message to the WebSocket (for ping/pong health checks)
   */
  const sendMessage = useCallback((message: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(message);
    }
  }, []);

  // AUTO-CONNECT: Establish connection when hook is used
  useEffect(() => {
    connect();
    
    // HEALTH CHECK: Send periodic ping to keep connection alive
    const pingInterval = setInterval(() => {
      sendMessage('ping');
    }, 30000); // Ping every 30 seconds

    // CLEANUP: Disconnect when component unmounts
    return () => {
      clearInterval(pingInterval);
      disconnect();
    };
  }, [connect, disconnect, sendMessage]);

  return {
    connectionStatus,
    lastEvent,
    connect,
    disconnect,
    sendMessage,
    isConnected: connectionStatus === 'connected'
  };
};